import {
  PATTERN_LIBRARY_PAGE_SIZE,
  PatternLibraryState,
} from 'pages/Schedule/store/patternLibrary.types';
import { create } from 'zustand';
import {
  ScheduleByLinePatternsQuery,
  ScheduleByLinePatternsQueryVariables,
  B2bCustomer,
  SecondaryUser,
  SortDirectionEnum,
  PatternSortableFieldsEnum,
  ByLinePatternShift,
  ByLinePattern,
  PatternLibraryPatternTotalsQuery,
  PatternLibraryPatternTotalsQueryVariables,
  PatternLibraryCustomFieldsQuery,
  PatternLibraryCustomFieldsQueryVariables,
} from 'types/graphql';
import {
  QUERY_patternLibraryPatternTotals,
  QUERY_scheduleByLinePatterns,
  QUERY_patternLibraryCustomFields,
  QUERY_patternLibraryPatternDetail,
} from 'queries';
import { handleGenericErrorMessage } from 'utils/handleGenericErrorMessage';
import { devtoolsInNonProd } from 'store/zustand/index.utils';
import { ApolloClient } from '@apollo/client';

const DEFAULT_SORT = [
  {
    field: PatternSortableFieldsEnum.created_at,
    direction: SortDirectionEnum.desc,
  },
];

function buildQueryVariables(
  searchQuery: string,
  extraVars?: Partial<ScheduleByLinePatternsQueryVariables>,
): ScheduleByLinePatternsQueryVariables {
  return {
    first: PATTERN_LIBRARY_PAGE_SIZE,
    sort: DEFAULT_SORT,
    ...(searchQuery.trim() ? { filter: { withName: searchQuery.trim() } } : {}),
    ...extraVars,
  };
}

function parsePatternListResponse(data: ScheduleByLinePatternsQuery) {
  const me = data?.me as B2bCustomer | SecondaryUser;
  const patterns = me?.byLinePatterns?.nodes || [];
  const pageInfo = me?.byLinePatterns?.pageInfo ?? null;
  return {
    patterns,
    pageInfo: pageInfo
      ? { endCursor: pageInfo.endCursor, hasNextPage: pageInfo.hasNextPage }
      : null,
  };
}

async function fetchPatternList(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  apolloClient: ApolloClient<any>,
  variables: ScheduleByLinePatternsQueryVariables,
) {
  const { data } = await apolloClient.query<
    ScheduleByLinePatternsQuery,
    ScheduleByLinePatternsQueryVariables
  >({
    query: QUERY_scheduleByLinePatterns,
    fetchPolicy: 'network-only',
    variables,
  });
  return parsePatternListResponse(data);
}

export const patternLibraryStore = create<PatternLibraryState>()(
  devtoolsInNonProd((set, get) => ({
    data: [],
    loading: false,
    error: null,
    hasInitialized: false,

    // Pagination state
    pageInfo: null,
    isLoadingMore: false,

    // Pattern detail
    activePatternDetailLoading: false,
    patternDetailLoaded: {},

    // Custom fields state
    customFieldsData: {},
    customFieldsLoading: {},
    customFieldsInitialized: {},

    // Actions
    setLoading: (loading) => set({ loading }),
    setError: (error) => set({ error }),

    fetchData: async (apolloClient) => {
      const { hasInitialized, loading } = get();

      if (hasInitialized || loading) return;

      set({
        loading: true,
        error: null,
        customFieldsData: {},
        customFieldsLoading: {},
        customFieldsInitialized: {},
      });

      try {
        const { patterns, pageInfo } = await fetchPatternList(
          apolloClient,
          buildQueryVariables(''),
        );

        set({
          data: patterns,
          pageInfo,
          loading: false,
          hasInitialized: true,
        });
      } catch (error) {
        handleGenericErrorMessage(error);
        set({ error, loading: false });
      }
    },

    loadMore: async (apolloClient, searchQuery = '') => {
      const { pageInfo, isLoadingMore } = get();

      if (isLoadingMore || !pageInfo?.hasNextPage) return;

      set({ isLoadingMore: true });

      try {
        const { patterns, pageInfo: newPageInfo } = await fetchPatternList(
          apolloClient,
          buildQueryVariables(searchQuery, { after: pageInfo.endCursor }),
        );

        set((state) => ({
          data: [...state.data, ...patterns],
          pageInfo: newPageInfo,
          isLoadingMore: false,
        }));
      } catch (error) {
        handleGenericErrorMessage(error);
        set({ isLoadingMore: false });
      }
    },

    refetch: async (apolloClient, searchQuery = '') => {
      const { data: oldData } = get();

      set({ loading: true, error: null });

      try {
        const { patterns, pageInfo } = await fetchPatternList(
          apolloClient,
          buildQueryVariables(searchQuery),
        );

        // Preserve shift detail from previously loaded patterns since the list
        // query no longer returns patternShifts / patternShiftHours.
        const oldByRelayId = new Map(
          oldData.filter((p) => p.patternShifts).map((p) => [p.relayId, p]),
        );
        const mergedPatterns = patterns.map((pattern) => {
          const old = oldByRelayId.get(pattern.relayId);
          return old
            ? {
                ...pattern,
                patternShifts: old.patternShifts,
                patternShiftHours: old.patternShiftHours,
              }
            : pattern;
        });

        set({
          data: mergedPatterns,
          pageInfo,
          loading: false,
        });
      } catch (error) {
        handleGenericErrorMessage(error);
        set({ error, loading: false });
      }
    },

    search: async (apolloClient, searchQuery) => {
      set({
        loading: true,
        error: null,
        data: [],
        pageInfo: null,
        patternDetailLoaded: {},
      });

      try {
        const { patterns, pageInfo } = await fetchPatternList(
          apolloClient,
          buildQueryVariables(searchQuery),
        );

        set({
          data: patterns,
          pageInfo,
          loading: false,
          hasInitialized: true,
        });
      } catch (error) {
        handleGenericErrorMessage(error);
        set({ error, loading: false });
      }
    },

    fetchPatternDetail: async (apolloClient, patternRelayId: string) => {
      const { patternDetailLoaded } = get();

      if (patternDetailLoaded[patternRelayId]) return;

      set({ activePatternDetailLoading: true });

      const markLoaded = () =>
        set((state) => ({
          activePatternDetailLoading: false,
          patternDetailLoaded: { ...state.patternDetailLoaded, [patternRelayId]: true },
          data: state.data.map((pattern) =>
            pattern.relayId === patternRelayId && !pattern.patternShifts
              ? { ...pattern, patternShifts: [] as ByLinePatternShift[] }
              : pattern,
          ),
        }));

      try {
        const { data } = await apolloClient.query({
          query: QUERY_patternLibraryPatternDetail,
          fetchPolicy: 'network-only',
          variables: { id: patternRelayId },
        });

        const patternNode = data?.node as ByLinePattern | undefined;

        if (patternNode) {
          set((state) => ({
            data: state.data.map((pattern) => {
              if (pattern.relayId === patternRelayId) {
                return {
                  ...pattern,
                  patternShifts: patternNode.patternShifts || [],
                  patternShiftHours: patternNode.patternShiftHours,
                };
              }
              return pattern;
            }),
            activePatternDetailLoading: false,
            patternDetailLoaded: { ...state.patternDetailLoaded, [patternRelayId]: true },
          }));
        } else {
          markLoaded();
        }
      } catch (error) {
        handleGenericErrorMessage(error);
        markLoaded();
      }
    },

    refetchPatternShiftHours: async (apolloClient, patternRelayId: string) => {
      try {
        const { data } = await apolloClient.query<
          PatternLibraryPatternTotalsQuery,
          PatternLibraryPatternTotalsQueryVariables
        >({
          query: QUERY_patternLibraryPatternTotals,
          fetchPolicy: 'network-only',
          variables: {
            id: patternRelayId,
          },
        });

        const patternNode = data?.node as ByLinePattern;

        const hoursByRotationWeek = patternNode?.patternShiftHours?.hoursByRotationWeek || [];

        if (hoursByRotationWeek?.length > 0)
          set((state) => ({
            data: state.data.map((pattern) => {
              if (pattern.relayId === patternRelayId) {
                return {
                  ...pattern,
                  patternShiftHours: {
                    ...pattern.patternShiftHours,
                    hoursByRotationWeek,
                  },
                };
              }
              return pattern;
            }),
          }));
      } catch (error) {
        handleGenericErrorMessage(error);
      }
    },

    fetchAvailableCustomFields: async (apolloClient, patternRelayId: string) => {
      const { customFieldsInitialized, customFieldsLoading } = get();

      if (customFieldsInitialized[patternRelayId] || customFieldsLoading[patternRelayId]) {
        return;
      }

      set((state) => ({
        customFieldsLoading: {
          ...state.customFieldsLoading,
          [patternRelayId]: true,
        },
      }));

      try {
        const { data } = await apolloClient.query<
          PatternLibraryCustomFieldsQuery,
          PatternLibraryCustomFieldsQueryVariables
        >({
          query: QUERY_patternLibraryCustomFields,
          fetchPolicy: 'network-only',
          variables: {
            id: patternRelayId,
          },
        });

        const patternNode = data?.node as ByLinePattern;
        const availableCustomFields = patternNode?.availableCustomFields || [];

        set((state) => ({
          customFieldsData: {
            ...state.customFieldsData,
            [patternRelayId]: availableCustomFields,
          },
          customFieldsLoading: {
            ...state.customFieldsLoading,
            [patternRelayId]: false,
          },
          customFieldsInitialized: {
            ...state.customFieldsInitialized,
            [patternRelayId]: true,
          },
        }));
      } catch (error) {
        handleGenericErrorMessage(error);
        set((state) => ({
          customFieldsLoading: {
            ...state.customFieldsLoading,
            [patternRelayId]: false,
          },
        }));
      }
    },

    addPatternShift: (patternId: string, patternShift: ByLinePatternShift) =>
      set((state) => ({
        data: state.data.map((pattern) => {
          if (pattern.relayId === patternId) {
            return {
              ...pattern,
              patternShifts: [...(pattern.patternShifts || []), patternShift],
            };
          }
          return pattern;
        }),
      })),

    updatePatternShift: (
      patternId: string,
      originalShift: ByLinePatternShift,
      updatedShift: ByLinePatternShift,
    ) =>
      set((state) => ({
        data: state.data.map((pattern) => {
          if (pattern.relayId === patternId) {
            return {
              ...pattern,
              patternShifts: (pattern.patternShifts || []).map((shift) =>
                shift.relayId === originalShift.relayId ? updatedShift : shift,
              ),
            };
          }
          return pattern;
        }),
      })),

    deletePatternShift: (patternId: string, patternShiftId: string) =>
      set((state) => ({
        data: state.data.map((pattern) => {
          if (pattern.relayId === patternId) {
            return {
              ...pattern,
              patternShifts: (pattern.patternShifts || []).filter(
                (patternShift) => patternShift.relayId !== patternShiftId,
              ),
            };
          }
          return pattern;
        }),
      })),

    addPattern: (pattern: ByLinePattern) => set((state) => ({ data: [pattern, ...state.data] })),
    updatePattern: (updatedFields: {
      relayId: string;
      name?: string;
      rotation?: number;
      isPrivate?: boolean;
    }) =>
      set((state) => ({
        data: state.data.map((p) =>
          p.relayId === updatedFields.relayId
            ? {
                ...p,
                ...(updatedFields.name !== undefined && { name: updatedFields.name }),
                ...(updatedFields.rotation !== undefined && { rotation: updatedFields.rotation }),
                ...(updatedFields.isPrivate !== undefined && {
                  isPrivate: updatedFields.isPrivate,
                }),
              }
            : p,
        ),
      })),

    deletePattern: (patternDisplayId: number) =>
      set((state) => ({
        data: state.data.filter((p) => p.displayId !== patternDisplayId),
        customFieldsData: Object.fromEntries(
          Object.entries(state.customFieldsData).filter(([key]) => {
            const pattern = state.data.find((p) => p.displayId === patternDisplayId);
            return pattern ? key !== pattern.relayId : true;
          }),
        ),
        customFieldsLoading: Object.fromEntries(
          Object.entries(state.customFieldsLoading).filter(([key]) => {
            const pattern = state.data.find((p) => p.displayId === patternDisplayId);
            return pattern ? key !== pattern.relayId : true;
          }),
        ),
        customFieldsInitialized: Object.fromEntries(
          Object.entries(state.customFieldsInitialized).filter(([key]) => {
            const pattern = state.data.find((p) => p.displayId === patternDisplayId);
            return pattern ? key !== pattern.relayId : true;
          }),
        ),
      })),

    reset: () =>
      set({
        data: [],
        loading: false,
        error: null,
        hasInitialized: false,
        pageInfo: null,
        isLoadingMore: false,
        activePatternDetailLoading: false,
        patternDetailLoaded: {},
        customFieldsData: {},
        customFieldsLoading: {},
        customFieldsInitialized: {},
      }),
  })),
);

export const usePatternLibraryStore = patternLibraryStore;
